// محلّل السطر النصي — قواعد فقط، بلا ذكاء اصطناعي وبلا إنترنت وبلا تكلفة.
//
// يعمل لأن القائمتين منفصلتان لا تتقاطعان: كل كلمة تعرف بُعدها بنفسها،
// فيفقد ترتيب الكلمات أهميته — «قرطاسية آية ٥» و«آية ٥ قرطاسية» متطابقتان.
// هذا مستحيل في التصنيف الشجري، حيث «مدرسة أحمد» غامضة المستوى.

import { state, activeDests, activeTypes, baseCur } from './store.js';
import { autoDestFor } from './model.js';

const NUM_WORDS = {
  'صفر': 0, 'واحد': 1, 'وحده': 1, 'اثنين': 2, 'اتنين': 2, 'ثلاثه': 3, 'تلاته': 3,
  'اربعه': 4, 'خمسه': 5, 'سته': 6, 'سبعه': 7, 'ثمانيه': 8, 'تمانيه': 8, 'تسعه': 9,
  'عشره': 10, 'احدعش': 11, 'اتنعش': 12, 'خمستعش': 15, 'عشرين': 20, 'ثلاثين': 30,
  'تلاتين': 30, 'اربعين': 40, 'خمسين': 50, 'ستين': 60, 'سبعين': 70, 'ثمانين': 80,
  'تمانين': 80, 'تسعين': 90, 'ميه': 100, 'مئه': 100, 'مايه': 100, 'ميتين': 200,
  'مئتين': 200, 'الف': 1000, 'الفين': 2000
};

const CUR_WORDS = {
  'شيكل': 'ILS', 'شيقل': 'ILS', 'شواكل': 'ILS', 'شيكلات': 'ILS',
  'دينار': 'JOD', 'دنانير': 'JOD',
  'دولار': 'USD', 'دولارات': 'USD'
};

const INCOME_WORDS = ['دخل', 'راتب', 'مرتب', 'معاش', 'مدخول', 'قبضت', 'وصلني', 'استلمت'];
const STOP = ['و', 'في', 'من', 'الى', 'ال', 'ب', 'عن', 'ع', 'لل', 'يوم', 'اليوم',
              'صرفت', 'دفعت', 'اشتريت', 'على', 'عند'];

/** توحيد الحروف والأرقام العربية حتى تتطابق المقارنة. */
export function normalize(s){
  return String(s || '')
    .replace(/[ً-ْـ]/g, '')                 // تشكيل وتطويل
    .replace(/[أإآٱ]/g, 'ا')           // همزات الألف
    .replace(/ى/g, 'ي')                               // ألف مقصورة
    .replace(/ة/g, 'ه')                               // تاء مربوطة
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/[٠-٩]/g, d => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48))
    .replace(/[۰-۹]/g, d => String.fromCharCode(d.charCodeAt(0) - 0x06F0 + 48))
    .toLowerCase();
}

function tokenize(text){
  return normalize(text)
    .replace(/[،,.؟!:;\-—–]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * واو العطف تلتصق بالكلمة في العربية: «وعلي» و«وخمسين» و«ومحروقات».
 * نجرّب كل كلمة بصورتيها — الأصل أولاً حتى لا نكسر «واحد» و«وقود».
 */
function variants(word){
  if (word.length > 3 && word[0] === 'و') return [word, word.slice(1)];
  return [word];
}

function numValue(word){
  for (const v of variants(word)){
    if (/^\d+(\.\d+)?$/.test(v)) return parseFloat(v);
    if (NUM_WORDS[v] !== undefined) return NUM_WORDS[v];
  }
  return null;
}

function readNumber(tokens, i){
  const a = numValue(tokens[i]);
  if (a === null) return null;
  if (/^\d/.test(tokens[i])) return { value: a, used: 1 };

  const joinable = b => (a < 10 && b >= 20 && b < 100) || (a >= 100 && b > 0 && b < 100);

  if (tokens[i + 1] === 'و'){
    const b = numValue(tokens[i + 2]);
    if (b !== null && joinable(b)) return { value: a + b, used: 3 };
  }
  if (tokens[i + 1] && tokens[i + 1][0] === 'و' && tokens[i + 1].length > 3){
    const b = numValue(tokens[i + 1].slice(1));
    if (b !== null && joinable(b)) return { value: a + b, used: 2 };
  }
  return { value: a, used: 1 };
}

function matchInList(list, words){
  let best = null, bestLen = 0;
  const candidates = words.flatMap(variants);
  for (const item of list){
    const parts = normalize(item.name).split(/\s+/).filter(p => p.length >= 2);
    for (const part of parts){
      for (const w of candidates){
        const hit = w === part ||
          (w.length >= 3 && part.length >= 3 && (part.startsWith(w) || w.startsWith(part)));
        if (hit && part.length > bestLen){ best = item; bestLen = part.length; }
      }
    }
  }
  return best;
}

/** يحلّل سطراً إلى صفوف جاهزة للحفظ. */
export function parseLine(text){
  const tokens = tokenize(text);
  const dests = activeDests();
  const types = activeTypes();

  const income = tokens.some(t => INCOME_WORDS.some(w => variants(t).includes(normalize(w))));

  const rows = [];
  let buffer = [];
  let headType = null, headDest = null, seenNumber = false;
  let cur = baseCur();
  const mentioned = new Set();

  for (let i = 0; i < tokens.length; i++){
    const t = tokens[i];

    const curHit = variants(t).map(x => CUR_WORDS[x]).find(Boolean);
    if (curHit){ cur = curHit; mentioned.add(curHit); continue; }

    const num = readNumber(tokens, i);
    if (num === null){
      const isIncomeWord = INCOME_WORDS.some(w => variants(t).includes(normalize(w)));
      if (!STOP.includes(t) && !isIncomeWord) buffer.push(t);
      continue;
    }
    i += num.used - 1;

    const words = buffer.slice();
    const d = matchInList(dests, words);
    const ty = matchInList(types, words);

    // ما ورد قبل أول رقم يصبح ترويسة تنطبق على ما بعده
    if (!seenNumber){
      if (ty && !d) headType = ty;
      if (d && !ty) headDest = d;
      if (d && ty){ headDest = d; headType = ty; }
    }
    seenNumber = true;

    const typeId = (ty || headType) ? (ty || headType).id : null;
    let destId = d ? d.id : (headDest ? headDest.id : null);
    let guessed = false;
    if (!destId && typeId){ destId = autoDestFor(typeId); guessed = !!destId; }
    if (!destId && income && dests.length){ destId = dests[0].id; guessed = true; }

    rows.push({
      amount: num.value,
      currency: cur,
      destId,
      typeId,
      guessed,
      kind: income ? 'in' : 'out',
      raw: words.join(' '),
      issue: destId ? null : (words.length ? `لم أتعرّف على «${words.join(' ')}»` : 'لم أعرف الوجهة')
    });
    buffer = [];
  }

  // نوع أو وجهة وردا بعد آخر رقم ينطبقان على الصفوف الناقصة
  if (buffer.length && rows.length){
    const tailType = matchInList(types, buffer);
    const tailDest = matchInList(dests, buffer);
    rows.forEach(r => {
      if (tailType && !r.typeId) r.typeId = tailType.id;
      if (tailDest && !r.destId){ r.destId = tailDest.id; r.guessed = false; r.issue = null; }
    });
  }

  // «محروقات ١٥٠ دينار» — العملة تُذكر بعد الرقم عادةً.
  // إن ذُكرت عملة واحدة في السطر كله فهي تنطبق على كل الصفوف مهما كان موضعها.
  if (mentioned.size === 1){
    const only = [...mentioned][0];
    rows.forEach(r => { r.currency = only; });
  }

  return { rows, income };
}

/** اقتراحات إصلاح لصف لم تُعرف وجهته — تُعرض كأزرار بنقرة واحدة. */
export function suggestionsFor(row){
  const out = [];
  const words = normalize(row.raw || '').split(/\s+/).filter(Boolean);
  for (const d of activeDests()){
    const n = normalize(d.name);
    for (const w of words){
      if (w.length < 3) continue;
      if (levenshtein(w, n) <= 1 && !out.includes(d.name)) out.push(d.name);
    }
  }
  activeDests().slice(0, 3).forEach(d => { if (!out.includes(d.name)) out.push(d.name); });
  return out.slice(0, 4);
}

function levenshtein(a, b){
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > 2) return 9;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++){
    const row = [i];
    for (let j = 1; j <= b.length; j++){
      row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = row;
  }
  return prev[b.length];
}
